import { signal } from '@preact/signals-react';
import { GeneralApiService } from '@api_patient/general_api.service';
import { AppointmentAccessApiModel, AppointmentCalendarInfoApiModel } from '@api_patient/generated/general/models';
import { UnitsApiService } from '@api_patient/units_api.service';
import { HostingService } from '@application/hosting.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import { UserSettingsService } from '@feature/settings/user_settings.service';
import { ControllerProtocol, Injectable } from '@visiba/instantiation';
import { IDP_AUTHENTICATION_PROVIDER_ID } from '../../constants';
import { AuthService } from '@application/authentication/auth.service';
import { DateService } from '@application/time/date.service';
import { createCounterState } from '@application/counter';

@Injectable()
export class ViewGuestController implements ControllerProtocol {
	public availability = signal<Availability>('available');
	public flowData = signal<AsyncSignalValue<FlowData>>({
		value: null,
		flowState: 'pending',
	});
	public readonly policyDocuments = signal<{ privacyPolicy: string; termsOfService: string } | null>(null);
	private counterDisposer: Nullable<ReturnType<typeof createCounterState>[1]> = null;

	constructor(
		private readonly generalApiService: GeneralApiService,
		private readonly unitsApiService: UnitsApiService,
		private readonly universalConfig: UniversalConfig,
		private readonly userSettingsService: UserSettingsService,
		private readonly hostingService: HostingService,
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
	) {
		// Empty
	}

	async whenMount(callticket: string) {
		const [accessInfoResult, calendarInfoResult, configResult, policyDocumentsResult] = await Promise.all([
			this.generalApiService.getAppointmentAccessInfo(callticket),
			this.generalApiService.getCalendarInfo(callticket),
			// We're intentionally over fetching here. It's not good but the way we've orchestrated the data flow.
			this.unitsApiService.getUnitConfig(this.universalConfig.currentUnitId ?? -1),
			this.generalApiService.getPolicyDocuments(this.universalConfig.alias, this.universalConfig.selectedLanguageCode.peek()),
		]);

		if (configResult.err || configResult.ok.data.Offer == null) {
			this.presentError();

			return;
		}

		if (policyDocumentsResult.ok) {
			this.policyDocuments.value = {
				privacyPolicy: policyDocumentsResult.ok.data.PrivacyPolicyUrl,
				termsOfService: policyDocumentsResult.ok.data.TermsOfServiceUrl,
			};
		}

		const unitName = configResult.ok.data.Unit.Name;

		if (calendarInfoResult.ok?.data.Completed) {
			this.availability.value = 'completed';
			this.flowData.value = {
				value: {
					receptionName: unitName,
					accessInfo: {
						AuthenticationMethod: null,
						ClientId: null,
						IdpAuth: null,
						OutsiderId: null,
						ResourceId: null,
					},
					calendarInfo: calendarInfoResult.ok.data,
				},
				flowState: 'accessible',
			};

			return;
		}

		if (calendarInfoResult.ok?.data.Cancelled) {
			this.availability.value = 'cancelled';
			this.flowData.value = {
				value: {
					receptionName: unitName,
					accessInfo: {
						AuthenticationMethod: null,
						ClientId: null,
						IdpAuth: null,
						OutsiderId: null,
						ResourceId: null,
					},
					calendarInfo: calendarInfoResult.ok.data,
				},
				flowState: 'accessible',
			};

			return;
		}

		if (calendarInfoResult.err != null || accessInfoResult.err != null) {
			this.presentError();

			return;
		}

		const accessInfoData = accessInfoResult.ok.data;
		const calendarInfoData = calendarInfoResult.ok.data;

		const isAlreadySignedIn = this.authService.user.peek() != null && accessInfoData.AuthenticationMethod == null;
		if (isAlreadySignedIn) {
			const unit = this.unitService.getUnitNodeInTree(this.universalConfig.currentUnitId ?? -1);
			window.location.href = `${this.hostingService.location}/${unit.route}/appointment/${callticket}`;

			return;
		}

		if (accessInfoData.AuthenticationMethod == null) {
			this.presentError();

			return;
		}

		if (accessInfoData.AuthenticationMethod.AuthenticationProviderId !== IDP_AUTHENTICATION_PROVIDER_ID) {
			const unit = this.unitService.getUnitNodeInTree(this.universalConfig.currentUnitId ?? -1);
			window.location.href = `${this.hostingService.location}/${unit.route}/guest-participant/legacy/${callticket}`;

			return;
		}

		const lobbyOpenAt = DateService.newDate(calendarInfoData.AppointmentStart).add(-15, 'minutes');
		const cannotJoinYet = DateService.newDate().isBefore(lobbyOpenAt);
		if (cannotJoinYet) {
			this.availability.value = 'waitingForOpen';

			const [counter, disposer] = createCounterState(lobbyOpenAt);
			this.counterDisposer = disposer;
			counter.subscribe(() => {
				const state = counter.read();
				if (state.hasElapse) {
					this.availability.value = 'available';
				}
			});

			this.flowData.value = {
				value: {
					receptionName: unitName,
					accessInfo: accessInfoData,
					calendarInfo: calendarInfoData,
				},
				flowState: 'accessible',
			};

			return;
		}

		this.availability.value = 'available';
		this.flowData.value = {
			value: {
				receptionName: unitName,
				accessInfo: accessInfoData,
				calendarInfo: calendarInfoData,
			},
			flowState: 'accessible',
		};
	}

	whenDemount() {
		this.counterDisposer?.();
	}

	public selectMethod(remoteScheme: string, callticket: string) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.currentUnitId ?? -1);

		const params = new URLSearchParams();
		params.append('returnUrl', `/${unit.route}/appointment/${callticket}`);
		params.append('languageCode', this.userSettingsService.getSettings().selectedLanguage);
		params.append('outsiderType', 'booking');
		params.append('outsiderId', callticket);

		window.location.href = `${this.hostingService.location}/api/auth/${
			this.universalConfig.licenceHolderId
		}/outsider/${remoteScheme.toLowerCase()}/challenge?${params.toString()}`;
	}

	private presentError() {
		this.flowData.value = {
			value: null,
			flowState: 'error',
		};
	}
}

export interface FlowData {
	receptionName: string;
	accessInfo: AppointmentAccessApiModel;
	calendarInfo: AppointmentCalendarInfoApiModel;
}

export interface Method {
	scheme: string;
	name: string;
	description: string;
	useIframe: boolean;
}

type Availability = 'available' | 'waitingForOpen' | 'completed' | 'cancelled' | 'closed';
